import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { DataFlowBg } from "@/components/DataFlowBg";
import { IMG } from "@/constants/brand";
import {
  Broadcast,
  UsersFour,
  ChartBar,
  ShieldWarning,
  ArrowsClockwise,
  Globe,
  WifiHigh,
  DeviceMobile,
  Coins,
  Percent,
  Wallet,
  AirplaneTilt,
  MagnifyingGlass,
  ArrowRight,
  Check,
  CircleNotch,
  Cpu,
  Lightning,
  MapPin,
  Clock,
  StackSimple,
  Timer,
  EyeSlash,
  Database,
} from "@phosphor-icons/react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const MODULE_ICONS = {
  core: Cpu,
  subs: UsersFour,
  usage: ChartBar,
  fraud: ShieldWarning,
  inter: ArrowsClockwise,
  ott: Globe,
  qos: WifiHigh,
  ceir: DeviceMobile,
  rev: Coins,
  tarif: Percent,
  momo: Wallet,
  roam: AirplaneTilt,
  search: MagnifyingGlass,
};

const PRINCIPLE_ICONS = [Lightning, StackSimple, Cpu, Timer, MapPin, Clock, Database, EyeSlash, ArrowsClockwise, Check];

const Telecom = () => {
  const { t, lang } = useLang();
  const capabilities = t("telecom.capabilities");
  const modules = t("telecom.modules");
  const principles = t("telecom.principles");
  const revenue = t("telecom.revenue");

  return (
    <>
      {/* Hero */}
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-24 overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMG.telecomTower} alt="" className="w-full h-full object-cover opacity-15" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0f1826]/60 via-[#0f1826]/80 to-[#0f1826]" />
        </div>
        <DataFlowBg density={10} />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="section-eyebrow flex items-center gap-2"><Broadcast size={14} weight="duotone" /> {t("telecom.eyebrow")}</div>
          <h1 className="h-display mt-6 text-4xl sm:text-5xl lg:text-6xl text-white max-w-5xl">
            {t("telecom.title")}
          </h1>
          <p className="mt-6 text-[#0089C8] text-lg tracking-tight max-w-3xl">{t("telecom.subtitle")}</p>
          <p className="mt-6 max-w-3xl text-slate-300 text-base sm:text-lg leading-relaxed">{t("telecom.lead")}</p>

          <div className="mt-8 inline-flex items-center gap-2 border border-[#0089C8]/40 bg-[#0089C8]/5 px-4 py-2" data-testid="telecom-partner-badge">
            <span className="inline-block w-1.5 h-1.5 bg-[#FF6B35]" />
            <span className="text-[11px] uppercase tracking-[0.24em] text-[#0089C8]">{t("telecom.partnerBadge")}</span>
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="relative py-20 md:py-24 border-t border-white/[0.08]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="section-eyebrow">/ 01</div>
            <h2 className="h-display mt-5 text-3xl sm:text-4xl text-white">{t("telecom.capabilitiesTitle")}</h2>
            <p className="mt-6 text-sm text-slate-300 leading-relaxed">{t("telecom.extension")}</p>
          </div>
          <div className="lg:col-span-8">
            <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
              {capabilities.map((c, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-slate-200 leading-relaxed border-b border-white/[0.08] pb-3">
                  <span className="mt-1 text-[#0089C8] shrink-0"><Check size={14} weight="bold" /></span>
                  <span>{c}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Modules */}
      <section className="relative py-20 md:py-24 border-t border-white/[0.08] bg-[#131e30]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="section-eyebrow">/ 02</div>
          <h2 className="h-display mt-5 text-3xl sm:text-4xl text-white max-w-2xl">{t("telecom.modulesTitle")}</h2>

          {/* Bento grid for modules */}
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {modules.map((m, i) => {
              const Icon = MODULE_ICONS[m.key] || CircleNotch;
              return (
                <div
                  key={m.key}
                  data-testid={`telecom-module-${m.key}`}
                  className="sharp-card p-5 group card-hilite"
                >
                  <div className="flex items-center justify-between">
                    <Icon size={22} className="text-[#0089C8]" weight="duotone" />
                    <span className="font-mono text-[10px] text-[#FF6B35] tracking-wider">M{String(i + 1).padStart(2, "0")}</span>
                  </div>
                  <div className="mt-5 text-white font-heading text-base leading-tight">{m.name}</div>
                  <p className="mt-2 text-xs text-slate-300 leading-relaxed">{m.desc}</p>
                </div>
              );
            })}
          </div>

          {/* Accordion for expanded details */}
          <div className="mt-14">
            <div className="section-eyebrow mb-4">
              {lang === "fr" ? "Détails par module" : "Module details"}
            </div>
            <Accordion type="single" collapsible className="border border-white/[0.08] divide-y divide-white/5" data-testid="telecom-accordion">
              {modules.map((m) => (
                <AccordionItem key={m.key} value={m.key} className="border-0 px-5" data-testid={`telecom-accordion-${m.key}`}>
                  <AccordionTrigger className="text-white hover:text-[#0089C8] text-sm uppercase tracking-[0.18em] font-heading">
                    {m.name}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-300 text-sm leading-relaxed pb-5">
                    {m.long || m.desc}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Principles */}
      <section className="relative py-20 md:py-24 border-t border-white/[0.08]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="section-eyebrow">/ 03</div>
          <h2 className="h-display mt-5 text-3xl sm:text-4xl text-white max-w-2xl">{t("telecom.principlesTitle")}</h2>

          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-5 gap-4">
            {principles.map((p, i) => {
              const Icon = PRINCIPLE_ICONS[i] || Check;
              return (
                <div key={i} className="sharp-card p-5" data-testid={`telecom-principle-${i}`}>
                  <div className="flex items-center justify-between">
                    <Icon size={20} weight="duotone" className="text-[#0089C8]" />
                    <span className="font-mono text-[10px] text-[#FF6B35]">/ {String(i + 1).padStart(2, "0")}</span>
                  </div>
                  <div className="mt-4 text-white text-sm font-heading">{p.t}</div>
                  <p className="mt-2 text-[12px] text-slate-300 leading-relaxed">{p.d}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Revenue opportunities */}
      <section className="relative py-20 md:py-24 border-t border-white/[0.08] overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMG.revenue} alt="" className="w-full h-full object-cover opacity-[0.08]" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#0f1826]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="section-eyebrow">/ 04</div>
          <h2 className="h-display mt-5 text-3xl sm:text-4xl text-white max-w-2xl">{t("telecom.revenueTitle")}</h2>
          <p className="mt-6 max-w-2xl text-slate-300">{t("telecom.revenueLead")}</p>

          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {revenue.map((r, i) => (
              <div key={i} className="sharp-card p-6 flex flex-col justify-between" data-testid={`telecom-revenue-${i}`}>
                <div>
                  <div className="font-mono text-[#FF6B35] text-xs">€ · $ · CFA / {String(i + 1).padStart(2, "0")}</div>
                  <div className="mt-4 text-white font-heading text-lg">{r.t}</div>
                  <p className="mt-2 text-sm text-slate-300 leading-relaxed">{r.d}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-14 border-t border-white/[0.08] pt-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="text-slate-300 text-sm max-w-xl">
              {lang === "fr"
                ? "Nous partageons volontiers un cadrage confidentiel adapté à votre marché national."
                : "We are happy to share a confidential scoping tailored to your national market."}
            </div>
            <Link
              to="/contact"
              className="group inline-flex items-center gap-3 bg-[#FF6B35] hover:bg-[#E55A2B] text-white px-6 py-3.5 text-xs uppercase tracking-[0.24em] font-semibold transition-colors"
              data-testid="telecom-contact-cta"
            >
              {t("nav.contactCta")}
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default Telecom;
